import { 
  useMutation, 
  useQuery, 
  useQueryClient,
  UseMutationResult,
  UseQueryResult
} from '@tanstack/react-query';
import { workflowService } from '../services/api/workflowService';
import type {
  WorkflowRequest,
  WorkflowResponse,
  AnalysisRequest,
  AnalysisResponse,
  ThemeRequest,
  ThemeResponse,
  VerificationRequest,
  PromptTemplate,
  WorkflowPrompt
} from '../types/workflow';
import { useState, useEffect } from 'react';
import { getClientIdFromUrl } from '../utils/helpers';

// Query keys
export const workflowKeys = {
  all: ['workflow'] as const,
  analysis: (workflowId: string) => [...workflowKeys.all, 'analysis', workflowId] as const,
  templates: ['workflow', 'templates'] as const,
  results: (workflowId: string) => [...workflowKeys.all, 'results', workflowId] as const,
};

// Create workflow mutation
export const useCreateWorkflow = (): UseMutationResult<
  WorkflowResponse, 
  Error,
  WorkflowRequest
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: workflowService.createWorkflow,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    }
  });
};

// Start analysis mutation
export const useStartAnalysis = (): UseMutationResult<
  AnalysisResponse,
  Error,
  AnalysisRequest
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: workflowService.startAnalysis,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ 
        queryKey: workflowKeys.analysis(data.workflow_id)
      });
    }
  });
};

// Get analysis status query
export const useAnalysisStatus = (
  workflowId: string,
  enabled = true
): UseQueryResult<AnalysisResponse, Error> => {
  const clientId = getClientIdFromUrl();
  
  return useQuery({
    queryKey: workflowKeys.analysis(workflowId),
    queryFn: () => workflowService.getAnalysisStatus(workflowId, clientId),
    enabled,
    refetchInterval: (query) => {
      // Refetch every 5 seconds if status is 'processing'
      return query.state.data?.status === 'processing' ? 5000 : false;
    }
  });
};

// Add custom themes mutation
export const useAddCustomThemes = (): UseMutationResult<
  ThemeResponse,
  Error,
  ThemeRequest
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: workflowService.addCustomThemes,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ 
        queryKey: workflowKeys.analysis(data.workflow_id)
      });
    }
  });
};

// Verify analysis mutation
export const useVerifyAnalysis = (): UseMutationResult<
  AnalysisResponse,
  Error,
  VerificationRequest
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: workflowService.verifyAnalysis,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ 
        queryKey: workflowKeys.analysis(data.workflow_id)
      });
    }
  });
};

// Get prompt templates query
export const usePromptTemplates = (): UseQueryResult<PromptTemplate[], Error> => {
  return useQuery({
    queryKey: workflowKeys.templates,
    queryFn: workflowService.getPromptTemplates
  });
};

// Create prompt template mutation
export const useCreatePromptTemplate = (): UseMutationResult<
  PromptTemplate,
  Error,
  PromptTemplate
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: workflowService.createPromptTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.templates });
    }
  });
};

// Update workflow prompt mutation
export const useUpdateWorkflowPrompt = (): UseMutationResult<
  WorkflowPrompt,
  Error,
  { workflowId: string; data: WorkflowPrompt }
> => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ workflowId, data }) => workflowService.updateWorkflowPrompt(workflowId, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ 
        queryKey: workflowKeys.analysis(data.workflow_id)
      });
    }
  });
};

// Get workflow results query
export const useWorkflowResults = (
  workflowId: string,
  enabled = true
): UseQueryResult<any, Error> => {
  return useQuery({
    queryKey: workflowKeys.results(workflowId),
    queryFn: () => workflowService.getWorkflowResults(workflowId),
    enabled
  });
};

interface Workflow {
  id: string;
  status: string;
  client_id?: string;
  business_name?: string;
  industry?: string;
  analysis_results?: any;
  error?: string;
}

export function useWorkflow(workflowId?: string, isUsingDatabaseApproach = false) {
  const [workflow, setWorkflow] = useState<Workflow | DatabaseWorkflow | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  
  useEffect(() => {
    if (!workflowId) return;
    
    // Add a small delay before the first request to ensure backend has time to process
    const initialDelay = 1000; // 1 second delay
    
    const fetchWorkflow = async () => {
      try {
        setIsLoading(true);
        // Add a delay before the request to prevent race conditions
        await new Promise(resolve => setTimeout(resolve, initialDelay));
        
        // Get client ID from URL or localStorage
        const clientId = getClientIdFromUrl();
        console.log(`Fetching workflow ${workflowId} with client_id: ${clientId}`);
        
        // Pass clientId to the service call
        if (isUsingDatabaseApproach) { 
          try {
            // First, check if this is a UUID (external ID)
            if (workflowId.includes('-')) {
              // This looks like a UUID, try getDbWorkflowByExternal first
              console.log(`Trying external ID lookup for: ${workflowId}`);
              try {
                const response = await workflowService.getDbWorkflowByExternal(workflowId);
                setWorkflow(response);
              } catch (externalIdError) {
                console.error(`External ID lookup failed, trying regular workflow endpoint:`, externalIdError);
                // If that fails, fallback to the regular getDbWorkflow which now handles both types
                const response = await workflowService.getDbWorkflow(workflowId);
                setWorkflow(response);
              }
            } else {
              // Not a UUID, use regular workflow endpoint
              const response = await workflowService.getDbWorkflow(workflowId);
              setWorkflow(response);
            }
          } catch (dbError) {
            console.error(`All database workflow lookups failed:`, dbError);
            throw dbError;
          }
        } else {
          // Use the standard approach which has been updated to handle both ID types
          const response = await workflowService.getWorkflow(workflowId, clientId);
          setWorkflow(response);
        }
        
        setError(null); // Clear any previous errors
        setRetryCount(0); // Reset retry count on success
      } catch (err) {
        console.error('Error fetching workflow:', err);
        
        // If we haven't reached max retries, try again with exponential backoff
        if (retryCount < maxRetries) {
          setRetryCount(prevCount => prevCount + 1);
          // Don't set error yet, we'll retry
        } else {
          // Max retries reached, set error
          setError(err instanceof Error ? err : new Error('Failed to fetch workflow'));
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchWorkflow();
    
    // Add retry with exponential backoff
    if (retryCount > 0 && retryCount <= maxRetries) {
      const retryDelay = Math.pow(2, retryCount) * 1000; // exponential backoff
      const retryTimer = setTimeout(() => {
        fetchWorkflow();
      }, retryDelay);
      
      // Clean up timer if component unmounts
      return () => clearTimeout(retryTimer);
    }
  }, [workflowId, retryCount, isUsingDatabaseApproach]);
  
  return { workflow, isLoading, error };
} 